import {
  CloseOutlined,
  DownOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import { Button, Col, Dropdown, message, Modal, Row, Space, Spin } from "antd";
import dayjs from "dayjs";
import IRRExport from "../report/IRRExport";
import { useEffect, useState } from "react";
import { GET_INSTALLMENTS, HEADERS_EXPORT_DETAIL } from "../../../API/apiUrls";
import axios from "axios";
import ManualIRR from "./ManualIRR";

// ── Stat Chip ──────────────────────────────────────────────────
const StatChip = ({ label, value, color }) => (
  <div
    style={{
      background: "#f8f9fc",
      borderRadius: 10,
      padding: "12px 20px",
      minWidth: 160,
      border: "1px solid #eef0f6",
    }}
  >
    <div
      style={{
        fontSize: 11,
        color: "#aaa",
        marginBottom: 4,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
  </div>
);

// ── Map API field → display field ─────────────────────────────
const mapRow = (row) => ({
  period: row.month,
  due_date: row.due_date,
  opening_balance: row.begin_principal,
  interest: row.interest,
  principal: row.principal_paid,
  installment: row.monthly_payment,
  actual_payment: row.total_payment,
  closing_balance: row.remaining_principal,
});

const IRRDetail = ({ open, close, record }) => {
  const [, , currencyFormatPoint] = CurrencyFormat();
  const [loading, setLoading] = useState(false);
  const [arrTable, setArrTable] = useState([]);

  useEffect(() => {
    if (open && record?.id) queryData();
  }, [open, record]);

  const handleCancel = () => {
    setArrTable([]);
    close();
  };

  const queryData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(GET_INSTALLMENTS + record.id, {
        headers: HEADERS_EXPORT_DETAIL,
      });
      if (res?.data?.length === 0 || !res.data) {
        message.info("ไม่มีข้อมูล");
        handleCancel();
      } else {
        setArrTable(res.data ?? []);
      }
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  // ✅ แปลง API data เป็น display format
  const schedule = arrTable.map(mapRow);

  const { handlePrintPDF, handleExportExcel } = IRRExport(
    record,
    schedule,
    currencyFormatPoint,
  );

  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  // ── Summary คำนวณจาก arrTable จริง ───────────────────────
  const totalInterest = arrTable.reduce((s, r) => s + (r.interest ?? 0), 0);
  const totalPrincipal = arrTable.reduce(
    (s, r) => s + (r.principal_paid ?? 0),
    0,
  );
  const totalPayment = arrTable.reduce((s, r) => s + (r.total_payment ?? 0), 0);

  if (arrTable) {
    return (
      <Modal
        title={
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e" }}>
              ตารางคำนวณ IRR
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#aaa",
                fontWeight: 400,
                marginTop: 2,
              }}
            >
              ดึงข้อมูลประวัติ (คำนวณเมื่อ{" "}
              {dayjs(record.created_at).format("DD MMM YYYY HH:mm")})
            </div>
          </div>
        }
        open={open}
        onCancel={handleCancel}
        width="92%"
        style={{ top: 16 }}
        styles={{ body: { padding: "16px 24px" } }}
        footer={[
          <Button key="close" onClick={close} icon={<CloseOutlined />}>
            ปิด
          </Button>,
        ]}
      >
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          {/* ── Summary + Export ──────────────────────────────── */}
          <Row align="middle" style={{ marginBottom: 20 }}>
            <Col flex="auto">
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <StatChip
                  label="Total Interest"
                  value={currencyFormatPoint(record.total_interest_paid)}
                  color="#5b6ef5"
                />
                <StatChip
                  label="Effective Rate (%/yr)"
                  value={`${record.effective_rate} %`}
                  color="#2ecc71"
                />
                <StatChip
                  label="Flat Rate (%/yr)"
                  value={`${record.flat_rate} %`}
                  color="#3498db"
                />
              </div>
            </Col>
            <Col>
              <Dropdown
                menu={exportMenuItems}
                disabled={!schedule.length}
                trigger={["click"]}
              >
                <Button size="large" icon={<PrinterOutlined />}>
                  <Space>
                    พิมพ์ / Export
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* ── Table ──────────────────────────────────────────── */}
          <div
            style={{
              overflowX: "auto",
              borderRadius: 10,
              border: "1px solid #eef0f6",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: "#1a2332" }}>
                  {[
                    "งวดที่",
                    "วันครบกำหนด (Due Date)",
                    "เงินต้นยกมา",
                    "ดอกเบี้ย",
                    "ตัดเงินต้น",
                    "ค่างวด",
                    "ยอดชำระจริง",
                    "เงินต้นคงเหลือ",
                  ].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        padding: "11px 14px",
                        textAlign: i <= 1 ? "center" : "right",
                        fontWeight: 600,
                        color: "#c8d0e0",
                        whiteSpace: "nowrap",
                        fontSize: 12,
                        letterSpacing: "0.3px",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, i) => (
                  <tr
                    key={row.period ?? i}
                    style={{
                      background: i % 2 === 0 ? "#fff" : "#f9fbff",
                      borderBottom: "1px solid #f0f2f8",
                    }}
                  >
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "center",
                        color: "#666",
                        fontWeight: 600,
                      }}
                    >
                      {row.period}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "center",
                        color: "#444",
                      }}
                    >
                      {dayjs(row.due_date).format("DD MMM YYYY")}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        color: "#333",
                      }}
                    >
                      {currencyFormatPoint(row.opening_balance)}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        color: "#e74c3c",
                        fontWeight: 600,
                      }}
                    >
                      {currencyFormatPoint(row.interest)}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        color: "#3498db",
                        fontWeight: 600,
                      }}
                    >
                      {currencyFormatPoint(row.principal)}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        color: "#555",
                      }}
                    >
                      {currencyFormatPoint(row.installment)}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        fontWeight: 700,
                        color: "#2c3e50",
                      }}
                    >
                      {currencyFormatPoint(row.actual_payment)}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        color: "#555",
                      }}
                    >
                      {currencyFormatPoint(row.closing_balance)}
                    </td>
                  </tr>
                ))}
              </tbody>

              {/* ── Summary Row ──────────────────────────────────── */}
              {schedule.length > 0 && (
                <tfoot>
                  <tr
                    style={{
                      background: "#ecf0f1",
                      borderTop: "2px solid #2c3e50",
                    }}
                  >
                    <td
                      colSpan={2}
                      style={{
                        padding: "10px 14px",
                        textAlign: "center",
                        fontWeight: 700,
                        color: "#2c3e50",
                      }}
                    >
                      รวมทั้งหมด {schedule.length} งวด
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        textAlign: "right",
                        color: "#555",
                        fontWeight: 700,
                      }}
                    >
                      -
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        textAlign: "right",
                        color: "#e74c3c",
                        fontWeight: 700,
                      }}
                    >
                      {currencyFormatPoint(totalInterest)}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        textAlign: "right",
                        color: "#3498db",
                        fontWeight: 700,
                      }}
                    >
                      {currencyFormatPoint(totalPrincipal)}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        textAlign: "right",
                        color: "#555",
                        fontWeight: 700,
                      }}
                    >
                      {currencyFormatPoint(
                        arrTable.reduce(
                          (s, r) => s + (r.monthly_payment ?? 0),
                          0,
                        ),
                      )}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        textAlign: "right",
                        fontWeight: 700,
                        color: "#2c3e50",
                      }}
                    >
                      {currencyFormatPoint(totalPayment)}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        textAlign: "right",
                        color: "#555",
                        fontWeight: 700,
                      }}
                    >
                      -
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Spin>
      </Modal>
    );
  }
};

export default IRRDetail;
